import type { FC, SVGProps } from "react"
import { useEffect, useMemo, useRef } from "react"
import { Link } from "@tanstack/react-router"
import { IconMessagesFill18 as MessagesFill } from "nucleo-ui-fill-18"
import {
	IconCode2Outline18 as ApiCode,
	IconCubeOutline18 as FeatureRequests,
	IconLifeRingOutline18 as LifeRing,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import { defaultAiChatWelcomeMessage } from "@productlane/lib/src/ai-chat-defaults"
import { cn } from "@productlane/lib/src/cn"
import { starter } from "@productlane/lib/src/plans-client"
import { isShadowModeAvailable } from "@productlane/lib/src/shadow-mode-rollout"
import {
	Badge,
	Form,
	Input,
	Label,
	SlackIconGray,
	Switch,
	Textarea,
	useZodForm,
} from "@productlane/ui"
import { EnvelopeDuotoneIcon } from "@productlane/ui/src/icons/project/envelope-duotone-icon"
import { MessagesDuotoneIcon } from "@productlane/ui/src/icons/project/messages-duotone-icon"
import { MicrosoftTeamsIcon } from "@productlane/ui/src/icons/project/microsoftteams-icon"
import { mutators } from "@productlane/zero/mutators"

import { CollapsiblePromptTextarea } from "@/components/collapsible-prompt-textarea"
import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { docsDefaultExampleQuestions } from "@/constants"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { hasLinearConnection } from "@/lib/linear-connection"
import { useRequiredPlans } from "@/lib/plans"
import { useAutosaveText } from "@/lib/use-autosave-text"
import { useAiChatSettingsTranslations } from "@/lib/use-item-translations"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingsHeader } from "../settings-header"
import { SettingsRightRail } from "../settings-right-rail"
import { SettingItem } from "../settings-switches/setting-item"
import {
	SettingsCardInsetContext,
	SettingsContainer,
} from "../settings-switches/settings-container"
import { SettingsSection } from "../settings-switches/settings-section"
import { WidgetPreview } from "../widget-preview"
import { AiPausedBanner } from "./ai-paused-banner"
import { KnowledgeCoverageCard } from "./knowledge-coverage-card"
import { LanguageSelector, useSelectedLanguage } from "./language-selector"
import { LearnWritingStyleButton } from "./learn-writing-style-button"
import { ShadowModeCard } from "./shadow-mode-card"

export const AiChatSettingsFormSchema = z.object({
	createLinearIssues: z.boolean(),
	welcomeMessage: z.string(),
	exampleQuestions: z.array(z.string()).length(3),
	isEnabled: z.boolean(),
	customInstructions: z.string().optional(),
	aiAutoRepliesEnabled: z.boolean(),
	deploySlack: z.boolean(),
	deployTeams: z.boolean(),
	deployApi: z.boolean(),
	deploySupportPortal: z.boolean(),
	deployFeatureRequests: z.boolean(),
})

type DeployChannel = keyof Pick<
	z.infer<typeof AiChatSettingsFormSchema>,
	| "aiAutoRepliesEnabled"
	| "deploySlack"
	| "deployTeams"
	| "deployApi"
	| "deploySupportPortal"
	| "deployFeatureRequests"
>

// Live chat has no toggle: the agent answers there whenever it is enabled. It
// still gets a row so the card shows every channel the agent covers.
const DEPLOY_CHANNELS: {
	name?: DeployChannel
	Icon: FC<SVGProps<SVGSVGElement>>
	title: string
	description: string
}[] = [
	{
		Icon: MessagesDuotoneIcon,
		title: "Live chat",
		description: "Answers customers in the widget chat as they type.",
	},
	{
		name: "aiAutoRepliesEnabled",
		Icon: EnvelopeDuotoneIcon,
		title: "Email",
		description:
			"Answers inbound email, and widget feedback that gets a reply by email.",
	},
	{
		name: "deploySlack",
		Icon: SlackIconGray,
		title: "Slack",
		description: "Answers in the Slack Connect thread the customer wrote in.",
	},
	{
		name: "deployTeams",
		Icon: MicrosoftTeamsIcon,
		title: "Microsoft Teams",
		description: "Answers in the Teams channel the customer wrote in.",
	},
	{
		name: "deployApi",
		Icon: ApiCode,
		title: "API",
		description: "Answers threads created through the public API.",
	},
	{
		name: "deploySupportPortal",
		Icon: LifeRing,
		title: "Support portal",
		description: "Answers messages customers send from the support portal.",
	},
	{
		name: "deployFeatureRequests",
		Icon: FeatureRequests,
		title: "Feature requests",
		description:
			"Answers feature requests submitted in the portal. Upvotes stay untouched.",
	},
]

export function AiChatSettingsPage() {
	const workspace = useZeroWorkspace()
	const zero = useZero()

	const { passes: hasRequiredPlan } = useRequiredPlans(starter.andUp)

	const workspaceInfoQuery = api.workspace.info.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	const hasCursorConnected = workspaceInfoQuery.data?.cursorConnected ?? false

	// Lets the preview below answer before the agent is switched on, so the
	// setting can be tried out first.
	const aiPreviewTokenQuery = api.ai.getAiChatPreviewToken.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	const updateWorkspaceMutation = api.workspace.update.useMutation()

	const [selectedLanguage, setSelectedLanguage] = useSelectedLanguage()
	const enabledLanguages = workspace?.enabledLanguages ?? ["en"]

	const workspaceWithTranslations = useMemo(() => {
		if (!workspace?.aiChatSettings) return null
		const translations = (
			workspace as {
				aiChatSettingsTranslations?: readonly {
					id: string
					language: string
					welcomeMessage: string | null
					exampleQuestions: string[]
					workspaceId: string
					createdAt: number
					updatedAt: number
					version: number
					isDeleted: boolean
				}[]
			}
		)?.aiChatSettingsTranslations
		return {
			id: workspace.id,
			workspaceId: workspace.id,
			welcomeMessage: workspace.aiChatSettings.welcomeMessage,
			exampleQuestions: workspace.aiChatSettings.exampleQuestions,
			translations: (translations ?? []) as readonly {
				readonly id: string
				readonly language: string
				readonly welcomeMessage: string | null
				readonly exampleQuestions: string[]
				readonly workspaceId: string
				readonly createdAt: number
				readonly updatedAt: number
				readonly version: number
				readonly isDeleted: boolean
			}[],
		}
	}, [workspace])

	const { selectedTranslation, defaultLanguage } =
		useAiChatSettingsTranslations(
			workspaceWithTranslations ?? {
				id: "",
				workspaceId: "",
				welcomeMessage: "",
				exampleQuestions: [],
				translations: [],
			},
			selectedLanguage,
		)

	const defaultLanguageRef = useRef(defaultLanguage)
	useEffect(() => {
		defaultLanguageRef.current = defaultLanguage
	}, [defaultLanguage])

	const hasLinearConnected = !!(
		hasLinearConnection(workspace) && workspace?.defaultLinearTeamId
	)

	const form = useZodForm({
		schema: AiChatSettingsFormSchema,
		defaultValues: {
			createLinearIssues: workspace?.aiChatSettings?.createLinearIssues ?? true,
			welcomeMessage:
				selectedTranslation.welcomeMessage ||
				defaultAiChatWelcomeMessage(workspace?.name),
			exampleQuestions: selectedTranslation.exampleQuestions?.length
				? selectedTranslation.exampleQuestions
				: docsDefaultExampleQuestions,
			isEnabled: workspace?.aiChatSettings?.isEnabled ?? false,
			customInstructions: workspace?.aiChatSettings?.customInstructions ?? "",
			aiAutoRepliesEnabled:
				workspace?.aiChatSettings?.aiAutoRepliesEnabled ?? false,
			deploySlack: workspace?.aiChatSettings?.deploySlack ?? false,
			deployTeams: workspace?.aiChatSettings?.deployTeams ?? false,
			deployApi: workspace?.aiChatSettings?.deployApi ?? false,
			deploySupportPortal:
				workspace?.aiChatSettings?.deploySupportPortal ?? false,
			deployFeatureRequests:
				workspace?.aiChatSettings?.deployFeatureRequests ?? false,
		},
	})

	const prevLanguageCodeRef = useRef<string | null>(null)
	const isTogglingRef = useRef(false)

	useEffect(() => {
		if (!workspace?.aiChatSettings) return
		if (isTogglingRef.current) return

		const isFirstRender = prevLanguageCodeRef.current === null
		const languageChanged =
			prevLanguageCodeRef.current !== selectedLanguage.code

		if (isFirstRender || languageChanged) {
			prevLanguageCodeRef.current = selectedLanguage.code

			form.reset({
				createLinearIssues: workspace.aiChatSettings.createLinearIssues ?? true,
				welcomeMessage:
					selectedTranslation.welcomeMessage ||
					defaultAiChatWelcomeMessage(workspace?.name),
				exampleQuestions: selectedTranslation.exampleQuestions?.length
					? Array.from(selectedTranslation.exampleQuestions)
					: docsDefaultExampleQuestions,
				isEnabled: workspace?.aiChatSettings?.isEnabled ?? false,
				customInstructions: workspace.aiChatSettings.customInstructions ?? "",
				aiAutoRepliesEnabled:
					workspace.aiChatSettings.aiAutoRepliesEnabled ?? false,
				deploySlack: workspace.aiChatSettings.deploySlack ?? false,
				deployTeams: workspace.aiChatSettings.deployTeams ?? false,
				deployApi: workspace.aiChatSettings.deployApi ?? false,
				deploySupportPortal:
					workspace.aiChatSettings.deploySupportPortal ?? false,
				deployFeatureRequests:
					workspace.aiChatSettings.deployFeatureRequests ?? false,
			})
		}
	}, [
		workspace,
		workspace?.aiChatSettings,
		form,
		selectedTranslation,
		selectedLanguage.code,
	])

	const persistAiChatSettings = async (
		partial: {
			createLinearIssues?: boolean
			isEnabled?: boolean
			customInstructions?: string | null
		} & Partial<Record<DeployChannel, boolean>>,
	) => {
		if (!workspace) return
		try {
			const settingsId = workspace.aiChatSettings?.id
			if (!settingsId) {
				const id = generateId()
				await mutateOk(
					zero.mutate(
						mutators.aiChatSettings.create({
							id,
							workspaceId: workspace.id,
							welcomeMessage:
								form.getValues("welcomeMessage") ||
								defaultAiChatWelcomeMessage(workspace.name),
							exampleQuestions:
								form.getValues("exampleQuestions")?.length === 3
									? Array.from(form.getValues("exampleQuestions"))
									: docsDefaultExampleQuestions,
							isEnabled: form.getValues("isEnabled"),
							createLinearIssues: form.getValues("createLinearIssues"),
							customInstructions: form.getValues("customInstructions") || null,
							aiAutoRepliesEnabled: form.getValues("aiAutoRepliesEnabled"),
							deploySlack: form.getValues("deploySlack"),
							deployTeams: form.getValues("deployTeams"),
							deployApi: form.getValues("deployApi"),
							deploySupportPortal: form.getValues("deploySupportPortal"),
							deployFeatureRequests: form.getValues("deployFeatureRequests"),
							...partial,
						}),
					),
				)
			} else {
				await mutateOk(
					zero.mutate(
						mutators.aiChatSettings.update({ id: settingsId, ...partial }),
					),
				)
			}
		} catch (error) {
			captureError(error, {
				domain: "ai-chat",
				action: "update ai chat settings",
				workspaceId: workspace?.id,
			})
			toast.error({
				title: "Failed to update",
				description:
					error instanceof Error ? error.message : "An error occurred",
			})
		}
	}

	const persistTranslatedContent = async () => {
		if (!workspace) return
		try {
			let settingsId = workspace.aiChatSettings?.id
			if (!settingsId) {
				const id = generateId()
				settingsId = id
				await mutateOk(
					zero.mutate(
						mutators.aiChatSettings.create({
							id,
							workspaceId: workspace.id,
							createLinearIssues: form.getValues("createLinearIssues"),
							welcomeMessage: form.getValues("welcomeMessage"),
							exampleQuestions: Array.from(form.getValues("exampleQuestions")),
							isEnabled: form.getValues("isEnabled"),
							customInstructions: form.getValues("customInstructions") || null,
							aiAutoRepliesEnabled: form.getValues("aiAutoRepliesEnabled"),
							deploySlack: form.getValues("deploySlack"),
							deployTeams: form.getValues("deployTeams"),
							deployApi: form.getValues("deployApi"),
							deploySupportPortal: form.getValues("deploySupportPortal"),
							deployFeatureRequests: form.getValues("deployFeatureRequests"),
						}),
					),
				)
			}

			const translations = (
				workspace as {
					aiChatSettingsTranslations?: readonly {
						language: string
						id: string
					}[]
				}
			)?.aiChatSettingsTranslations
			const existingTranslation = translations?.find(
				(t) => t.language === selectedLanguage.code,
			)
			const finalTranslationId =
				existingTranslation?.id ??
				(selectedTranslation.id.startsWith("temp-")
					? generateId()
					: selectedTranslation.id)

			await mutateOk(
				zero.mutate(
					mutators.aiChatSettingsTranslations.upsert({
						id: finalTranslationId,
						workspaceId: workspace.id,
						language: selectedLanguage.code,
						welcomeMessage: form.getValues("welcomeMessage"),
						exampleQuestions: Array.from(form.getValues("exampleQuestions")),
					}),
				),
			)

			if (selectedLanguage.code === defaultLanguageRef.current) {
				await mutateOk(
					zero.mutate(
						mutators.aiChatSettings.update({
							id: settingsId,
							welcomeMessage: form.getValues("welcomeMessage"),
							exampleQuestions: Array.from(form.getValues("exampleQuestions")),
						}),
					),
				)
			}
		} catch (error) {
			captureError(error, {
				domain: "ai-chat",
				action: "update ai chat settings",
				workspaceId: workspace?.id,
			})
			toast.error({
				title: "Failed to update",
				description:
					error instanceof Error ? error.message : "An error occurred",
			})
		}
	}

	const watchedWelcome = form.watch("welcomeMessage")
	const watchedExampleQuestions = form.watch("exampleQuestions")
	const watchedCustomInstructions = form.watch("customInstructions")

	// One save covers both, so they share a key rather than autosaving separately.
	const translatedKey = [
		watchedWelcome,
		...(watchedExampleQuestions ?? []),
	].join(" ")
	const persistedTranslatedKey = [
		selectedTranslation.welcomeMessage ?? "",
		...(selectedTranslation.exampleQuestions ?? []),
	].join(" ")

	useAutosaveText({
		value: translatedKey,
		persistedValue: persistedTranslatedKey,
		onSave: () => persistTranslatedContent(),
		enabled:
			!!workspace &&
			(!!form.formState.dirtyFields.welcomeMessage ||
				!!form.formState.dirtyFields.exampleQuestions),
	})

	useAutosaveText({
		value: watchedCustomInstructions ?? "",
		persistedValue: workspace?.aiChatSettings?.customInstructions ?? "",
		onSave: (value) =>
			persistAiChatSettings({ customInstructions: value || null }),
		enabled: !!workspace && !!form.formState.dirtyFields.customInstructions,
	})

	const { data: aiBudget } = api.workspace.getAiBudgetStatus.useQuery(
		undefined,
		{ staleTime: 30_000 },
	)

	if (!workspace) {
		return null
	}

	const { aiChatSettings } = workspace
	const widgetKey = workspace.widgetSettings?.widgetKey ?? null

	const previewSettingsHash = JSON.stringify({
		widgetKey,
		isEnabled: aiChatSettings?.isEnabled,
		aiAutoRepliesEnabled: aiChatSettings?.aiAutoRepliesEnabled,
		accentColor: workspace.accentColor,
		darkModeAccentColor: workspace.darkModeAccentColor,
		buttonTextColor: workspace.buttonTextColor,
		darkModeButtonTextColor: workspace.darkModeButtonTextColor,
		language: selectedLanguage.code,
	})

	const previewLiveSettings = {
		aiChatSettings: {
			welcomeMessage: watchedWelcome,
			exampleQuestions: Array.from(watchedExampleQuestions ?? []),
		},
	}

	const handleAiChatToggle = async (enabled: boolean) => {
		isTogglingRef.current = true

		if (!workspace) {
			isTogglingRef.current = false
			return
		}

		try {
			let settingsId = aiChatSettings?.id

			if (!settingsId) {
				const id = generateId()
				settingsId = id
				const welcomeMessage =
					form.getValues("welcomeMessage") ||
					`Hi! I'm an AI trained on help docs, roadmap and changelog. Ask me anything about ${workspace.name ?? "the product"}.`
				const exampleQuestions =
					form.getValues("exampleQuestions")?.length === 3
						? Array.from(form.getValues("exampleQuestions"))
						: docsDefaultExampleQuestions

				await mutateOk(
					zero.mutate(
						mutators.aiChatSettings.create({
							id,
							workspaceId: workspace.id,
							welcomeMessage,
							exampleQuestions,
							isEnabled: enabled,
						}),
					),
				)
			} else {
				await mutateOk(
					zero.mutate(
						mutators.aiChatSettings.update({
							id: settingsId,
							isEnabled: enabled,
						}),
					),
				)
			}

			form.setValue("isEnabled", enabled)
			toast.success({ title: enabled ? "AI Chat enabled" : "AI Chat disabled" })
			analytics.track("toggle-ai-chat", { enabled })
		} catch (error) {
			console.error("Error toggling AI chat:", error)
			captureError(error, {
				domain: "ai-chat",
				action: "toggle ai chat",
				workspaceId: workspace?.id,
			})
			toast.error({
				title: "Failed to update",
				description:
					error instanceof Error ? error.message : "An error occurred",
			})
			form.setValue("isEnabled", !enabled)
		} finally {
			isTogglingRef.current = false
		}
	}

	const explainLiveChatAlwaysOn = () => {
		toast.info({
			id: "agent-live-chat-always-on",
			title: "Live chat is always on",
			description:
				"The agent answers in the widget chat whenever it is enabled. Turn the agent off above to stop it there.",
			icon: MessagesFill,
		})
	}

	const isAgentEnabled = form.watch("isEnabled")
	const agentPausedByBudget = isAgentEnabled && !!aiBudget?.blocked
	const dimmedClass = cn(
		"transition-opacity duration-200 ease-in-out",
		!isAgentEnabled && "opacity-50",
	)

	return (
		<SettingsCardInsetContext.Provider value={false}>
			<MetaInfo title="Productlane Agent Settings" />

			<SettingsHeader
				title="Productlane Agent"
				subtitle="Resolve customer questions with answers grounded in your docs."
				docsArticle="c8cfc4d9-dec0-4188-b31c-f55ffd758d58"
			/>

			<div className="min-w-0">
				<Form
					className="flex flex-col gap-4"
					form={form}
					onSubmit={async () => {
						await persistTranslatedContent()
						await persistAiChatSettings({
							customInstructions: form.getValues("customInstructions") || null,
						})
						analytics.track("update-ai-chat-settings")
					}}
				>
					<SettingsContainer notice={<AiPausedBanner />}>
						<SettingItem
							title="Productlane Agent"
							description={
								agentPausedByBudget
									? "The switch is on, but the spend limit stopped the agent. Raise the limit to start it again."
									: "Activate the Productlane Agent for your widget, live chat and docs."
							}
							action={
								<div className="flex items-center gap-2">
									{/* An "on" switch over a paused agent is a lie, so the state
									    the budget put it in is named next to it. */}
									{agentPausedByBudget ? (
										<Badge variant="default">Paused</Badge>
									) : null}
									{hasRequiredPlan ? (
										<Switch
											name="isEnabled"
											label={null}
											checked={isAgentEnabled}
											onCheckedChange={handleAiChatToggle}
										/>
									) : (
										<UpgradeButton required={starter.andUp} />
									)}
								</div>
							}
						/>
					</SettingsContainer>

					{isShadowModeAvailable(workspace.id, {
						isDevelopment: import.meta.env.DEV,
					}) && (
						<ShadowModeCard
							agentEnabled={isAgentEnabled}
							shadowRepliesEnabled={aiChatSettings?.shadowRepliesEnabled}
							workspaceId={workspace.id}
						/>
					)}

					<KnowledgeCoverageCard />

					<SettingsSection
						className={dimmedClass}
						title="Deploy"
						description="Pick the channels the agent answers on. Upvotes and threads pushed from Linear stay with your team."
					/>
					<SettingsContainer className={cn("mt-1", dimmedClass)}>
						{DEPLOY_CHANNELS.map((channel, index) => {
							const { name } = channel
							return (
								<SettingItem
									key={channel.title}
									Icon={channel.Icon}
									title={channel.title}
									description={channel.description}
									className={index > 0 ? "pt-3" : undefined}
									action={
										<>
											{!hasRequiredPlan ? (
												<UpgradeButton required={starter.andUp} />
											) : name ? (
												<Switch
													name={name}
													checked={form.watch(name)}
													onCheckedChange={(newValue) => {
														form.setValue(name, newValue)
														void persistAiChatSettings({ [name]: newValue })
													}}
												/>
											) : (
												// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
												<div onClick={explainLiveChatAlwaysOn}>
													<Switch
														name="liveChat"
														checked
														disabled
														switchClassName="pointer-events-none"
													/>
												</div>
											)}
										</>
									}
								/>
							)
						})}
					</SettingsContainer>

					{hasLinearConnected && (
						<SettingsContainer className={dimmedClass}>
							<SettingItem
								title="Create Linear issues from agent chats"
								description={
									<>
										The agent files bugs and feature requests to Linear. Group
										reports in{" "}
										<Link
											to="/settings/ai/routines"
											className="text-primary underline"
										>
											Routines
										</Link>
										.
									</>
								}
								action={
									<>
										{hasRequiredPlan ? (
											<Switch
												name="createLinearIssues"
												checked={form.watch("createLinearIssues")}
												onCheckedChange={(newValue) => {
													form.setValue("createLinearIssues", newValue)
													void persistAiChatSettings({
														createLinearIssues: newValue,
													})
												}}
											/>
										) : (
											<UpgradeButton required={starter.andUp} />
										)}
									</>
								}
							/>
						</SettingsContainer>
					)}

					<SettingsContainer className={dimmedClass}>
						<SettingItem
							title="Auto-assign Productlane Agent to bugs"
							description={
								hasCursorConnected ? (
									"Automatically assign a coding agent to fix bug reports."
								) : (
									<>
										Requires a connected coding agent.{" "}
										<Link
											to="/settings/integrations/cursor"
											className="text-primary underline"
										>
											Connect Cursor
										</Link>{" "}
										to enable.
									</>
								)
							}
							action={
								<>
									{hasRequiredPlan ? (
										<Switch
											name="autoAssignAgentToBugs"
											checked={workspace?.autoAssignAgentToBugs ?? false}
											disabled={!hasCursorConnected}
											onCheckedChange={(newValue) => {
												updateWorkspaceMutation.mutate({
													autoAssignAgentToBugs: newValue,
												})
											}}
										/>
									) : (
										<UpgradeButton required={starter.andUp} />
									)}
								</>
							}
						/>
						{(workspace?.autoAssignAgentToBugs ?? false) && (
							<SettingItem
								title="Exclude manually created issues"
								description="Only auto-assign to bugs created by the AI chat, not from manual feedback."
								className="pt-3"
								action={
									<>
										{hasRequiredPlan ? (
											<Switch
												name="excludeManuallyCreatedIssues"
												checked={
													workspace?.excludeManuallyCreatedIssues ?? false
												}
												disabled={!hasCursorConnected}
												onCheckedChange={(newValue) => {
													updateWorkspaceMutation.mutate({
														excludeManuallyCreatedIssues: newValue,
													})
												}}
											/>
										) : (
											<UpgradeButton required={starter.andUp} />
										)}
									</>
								}
							/>
						)}
					</SettingsContainer>

					{hasRequiredPlan && (
						<>
							<SettingsSection
								className={dimmedClass}
								title="Customize"
								description="Customize the AI chat messages and suggestions."
								action={
									enabledLanguages.length > 1 ? (
										<LanguageSelector
											selectedLanguage={selectedLanguage}
											onLanguageSelect={setSelectedLanguage}
											languages={enabledLanguages}
											placeholder="Select language..."
										/>
									) : undefined
								}
							/>

							<SettingsContainer
								className={cn(
									"mt-1 flex flex-col gap-4 divide-none p-4",
									dimmedClass,
								)}
							>
								<Textarea
									variant="settings"
									{...form.register("welcomeMessage")}
									label="Welcome Message"
									placeholder="Welcome Message"
									onBlur={(e) => {
										form.setValue("welcomeMessage", e.target.value)
										void persistTranslatedContent()
									}}
								/>

								<div className="flex flex-col gap-2">
									<Label className="mb-0.5 block">Suggestions</Label>
									{form.watch("exampleQuestions")?.map((_, index) => (
										<div key={index}>
											<Input
												{...form.register(`exampleQuestions.${index}`)}
												placeholder={`Suggestion ${index + 1}`}
												onBlur={() => {
													void persistTranslatedContent()
												}}
											/>
										</div>
									))}
								</div>
							</SettingsContainer>

							<SettingsSection
								className={dimmedClass}
								title="Workspace guidance"
								description="Save instructions and writing preferences for the Productlane Agent across all channels. They apply until you change them."
							/>

							<div className={dimmedClass}>
								<CollapsiblePromptTextarea
									variant="settings"
									{...form.register("customInstructions")}
									placeholder="Optional agent guidance..."
									minRows={4}
									onBlur={(e) => {
										form.setValue("customInstructions", e.target.value)
										void persistAiChatSettings({
											customInstructions: e.target.value || null,
										})
									}}
								/>
								<LearnWritingStyleButton
									onApply={(style) => {
										const value = [
											form.getValues("customInstructions")?.trim(),
											style,
										]
											.filter(Boolean)
											.join("\n\n")
										form.setValue("customInstructions", value, {
											shouldDirty: true,
										})
									}}
								/>
							</div>
						</>
					)}
				</Form>
			</div>
			<SettingsRightRail>
				<WidgetPreview
					fill
					widgetKey={aiPreviewTokenQuery.isLoading ? null : widgetKey}
					settingsHash={previewSettingsHash}
					liveSettings={previewLiveSettings}
					view="ai"
					aiPreviewToken={aiPreviewTokenQuery.data?.token}
					emptyMessage={
						aiPreviewTokenQuery.isLoading
							? ""
							: "Generate the widget snippet to see a live preview here."
					}
				/>
			</SettingsRightRail>
		</SettingsCardInsetContext.Provider>
	)
}
